import React, { useState } from "react";
import { Alert, Box, Button, MenuItem, Select, Stack, TextField, Typography } from "@mui/material";
import { useCan } from "../../auth/Can";
import { SupportTicket } from "../../types/Support";
import { recordSuspendDecision, updateSupportTicket } from "../../services/supportService";

/** MVAG on a ticket: the 3-day misconduct inquiry (closed with an outcome the rider is told) and the suspend decision
 *  for zero tolerance / identity mismatch (recording only — staff act on the driver page, D17). */

export const INQUIRY_OUTCOME_LABELS: Record<string, string> = { upheld: "Upheld", not_upheld: "Not upheld", insufficient_evidence: "Not enough evidence" };
const fmt = (iso: string) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

const MvagPanel: React.FC<{ ticket: SupportTicket; onChange: (patch: Partial<SupportTicket>) => void; now?: number }> = ({ ticket, onChange, now = Date.now() }) => {
    const { can, canAny } = useCan();
    const [outcome, setOutcome] = useState("");
    const [reason, setReason] = useState("");
    const [error, setError] = useState<string | null>(null);
    if (!ticket.inquiryDueAt && !ticket.decisionDueAt) return null;

    const overdue = (iso?: string | null) => !!iso && new Date(iso).getTime() < now;
    const fail = (e: any) => setError(e?.response?.data?.message || "Could not save");

    const closeInquiry = async () => {
        try {
            await updateSupportTicket(ticket.id, { inquiryOutcome: outcome } as any);
            onChange({ inquiryOutcome: outcome, inquiryClosedAt: new Date().toISOString() });
            setError(null);
        } catch (e) { fail(e); }
    };
    const decide = async (decision: "suspend" | "no_suspend") => {
        try {
            await recordSuspendDecision(ticket.id, decision, reason.trim());
            onChange({ decision, decisionReason: reason.trim(), decidedAt: new Date().toISOString() });
            setError(null);
        } catch (e) { fail(e); }
    };

    return (
        <Box sx={{ mt: 2, p: 1.5, border: "1px solid", borderColor: "warning.light", borderRadius: 2 }} data-testid="mvag-panel">
            <Typography variant="caption" sx={{ fontWeight: 700 }}>DRIVER CONDUCT (MVAG)</Typography>
            {ticket.complainantHistory && (
                <Typography variant="body2" color="text.secondary">
                    This customer has raised {ticket.complainantHistory.tickets} ticket(s), {ticket.complainantHistory.misconduct} about driver conduct.
                </Typography>
            )}
            {ticket.decisionDueAt && (
                <Box sx={{ mt: 1 }}>
                    {ticket.decidedAt ? (
                        <Alert severity="info">Suspend decision: <strong>{ticket.decision === "suspend" ? "Suspend" : "No suspension"}</strong> — {ticket.decisionReason}
                            {ticket.decision === "suspend" && " · Suspend the driver from the driver page (nothing is automatic)."}</Alert>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ color: overdue(ticket.decisionDueAt) ? "error.main" : "text.primary", fontWeight: 600 }}>
                                Suspend decision due {fmt(ticket.decisionDueAt)}{overdue(ticket.decisionDueAt) ? " — OVERDUE" : ""}
                            </Typography>
                            {can("support:write") && canAny(["drivers:write", "drivers:suspend_inquiry"] as any) ? (
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
                                    <TextField size="small" label="Reason" value={reason} onChange={(e) => setReason(e.target.value)} inputProps={{ maxLength: 500 }} sx={{ minWidth: 220 }} />
                                    <Button size="small" variant="contained" color="error" disabled={!reason.trim()} onClick={() => decide("suspend")}>Suspend driver</Button>
                                    <Button size="small" variant="outlined" disabled={!reason.trim()} onClick={() => decide("no_suspend")}>No suspension</Button>
                                </Stack>
                            ) : (
                                <Typography variant="caption" color="text.secondary">Needs someone with the drivers:write or drivers:suspend_inquiry tick.</Typography>
                            )}
                        </>
                    )}
                </Box>
            )}
            {ticket.inquiryDueAt && (
                <Box sx={{ mt: 1 }}>
                    {ticket.inquiryClosedAt ? (
                        <Typography variant="body2">Inquiry closed: <strong>{INQUIRY_OUTCOME_LABELS[ticket.inquiryOutcome ?? ""] ?? ticket.inquiryOutcome}</strong> (the customer was told)</Typography>
                    ) : (
                        <>
                            <Typography variant="body2" sx={{ color: overdue(ticket.inquiryDueAt) ? "error.main" : "text.primary", fontWeight: 600 }}>
                                Inquiry due {fmt(ticket.inquiryDueAt)}{overdue(ticket.inquiryDueAt) ? " — OVERDUE" : ""}
                            </Typography>
                            {can("support:write") && (
                                <Stack direction="row" spacing={1} sx={{ mt: 1 }} alignItems="center">
                                    <Select size="small" displayEmpty value={outcome} onChange={(e) => setOutcome(String(e.target.value))} inputProps={{ "aria-label": "Inquiry outcome" }} sx={{ minWidth: 200 }}>
                                        <MenuItem value="" disabled>Outcome</MenuItem>
                                        {Object.entries(INQUIRY_OUTCOME_LABELS).map(([v, l]) => <MenuItem key={v} value={v}>{l}</MenuItem>)}
                                    </Select>
                                    <Button size="small" variant="contained" disabled={!outcome} onClick={closeInquiry}>Close inquiry</Button>
                                </Stack>
                            )}
                        </>
                    )}
                </Box>
            )}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
        </Box>
    );
};

export default MvagPanel;
